import React from 'react';
import classNames from 'classnames';

import Field, { hasDataInFields } from '../../Field';
import { Link } from '../../Primitives/Link';
import BlockContainer from '../BlockContainer';

import css from './BlockDefault.module.css';

const CATEGORY_SEARCH_HREF = {
  festmény: '/s?pub_categoryLevel1=festmeny',
  festmeny: '/s?pub_categoryLevel1=festmeny',
  grafika: '/s?pub_categoryLevel1=rajz',
  szobor: '/s?pub_categoryLevel1=szobor',
  fotó: '/s?pub_categoryLevel1=foto',
  foto: '/s?pub_categoryLevel1=foto',
  egyéb: '/s?pub_categoryLevel1=egyeb',
  egyeb: '/s?pub_categoryLevel1=egyeb',
};

const hrefFromString = value =>
  typeof value === 'string' && value.startsWith('/') ? value : null;

const searchHrefFromTitle = title => {
  const key = String(title?.content || '')
    .trim()
    .toLowerCase();
  return CATEGORY_SEARCH_HREF[key] || null;
};

const FieldMedia = props => {
  const { className, media, sizes, options } = props;
  const hasMediaField = hasDataInFields([media], options);
  return hasMediaField ? (
    <div className={classNames(className, css.media)}>
      <Field data={media} sizes={sizes} options={options} />
    </div>
  ) : null;
};

/**
 * @typedef {Object} FieldComponentConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * This returns a component that can render 'defaultBlock' config.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.blockId id from the block config
 * @param {string} props.blockName name from the block config (not used)
 * @param {'defaultBlock'} props.blockType blockType is set to 'defaultBlock'
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.title heading config for the block
 * @param {Object?} props.text content config for the block (can be markdown)
 * @param {Object?} props.callToAction call to action button (e.g. internal link config)
 * @param {string?} props.responsiveImageSizes
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentConfig>?} props.options.fieldComponents Custom fieldComponents
 * @returns {JSX.Element} component that renders block type: 'defaultBlock'
 */
const BlockDefault = props => {
  const {
    blockId,
    className,
    rootClassName,
    mediaClassName,
    textClassName,
    ctaButtonClass,
    title,
    text,
    callToAction,
    media,
    responsiveImageSizes,
    options = {},
    alignment,
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const hasTextComponentFields = hasDataInFields([title, text, callToAction], options);

  const alignmentClasses = {
    left: css.alignLeft,
    center: css.alignCenter,
    right: css.alignRight,
  };

  const alignmentClass = alignmentClasses[alignment];
  const href =
    hrefFromString(media?.link?.href) ||
    hrefFromString(callToAction?.href) ||
    searchHrefFromTitle(title);
  const mediaWithoutNestedLink = media ? { ...media, link: null } : media;

  const body = (
    <>
      <FieldMedia
        media={href ? mediaWithoutNestedLink : media}
        sizes={responsiveImageSizes}
        className={mediaClassName}
        options={options}
      />
      {hasTextComponentFields ? (
        <div className={classNames(textClassName, alignmentClass, css.text)}>
          <Field data={title} options={options} />
          <Field data={text} options={options} />
          {href ? null : <Field data={callToAction} className={ctaButtonClass} options={options} />}
        </div>
      ) : null}
    </>
  );

  return (
    <BlockContainer id={blockId} className={classes}>
      {href ? (
        <Link className={css.blockLink} href={href}>
          {body}
        </Link>
      ) : (
        body
      )}
    </BlockContainer>
  );
};

export default BlockDefault;
